import { useNavigation, useRoute } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useCallback, useMemo } from "react";
import type { RootStackParamList } from "../ui/navigation/Navigation";

type RouteName = keyof RootStackParamList;

type ScreenParamsPair = {
  // First we use a mapped type to get an union of screen & params pairs
  // Then we pick the pair which matches the RouteName
  // Mapped type is used instead of just ParamList[RouteName]
  // Otherwise it'll result in union of all params leading to incorrect types
  [Screen in keyof RootStackParamList]: undefined extends RootStackParamList[Screen] // Params are either undefined or a union with undefined
    ?
        | [screen: Screen] // if the params are optional, we don't have to provide it
        | [screen: Screen, params: RootStackParamList[Screen]]
    : [screen: Screen, params: RootStackParamList[Screen]];
}[RouteName];

export const usePlanNavigation = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<ReactNavigation.RootParamList>>();
  const route = useRoute();

  const getNavigationArgs = useCallback(
    (...args: ScreenParamsPair): ScreenParamsPair => {
      const params = route.params;
      if (params && "businessUid" in params) {
        const [goToRouteName, goToParams] = args;

        const businessParams = { businessUid: params.businessUid ?? "" };
        // biome-ignore lint/suspicious/noExplicitAny: Because
        const newParams = { ...goToParams, ...businessParams } as any;

        switch (goToRouteName) {
          case "Module":
            //
            return ["BusinessTeamModule", newParams];
          case "Learn":
            return ["BusinessLearn", newParams];
          case "PlanSettings":
            return ["BusinessTeamSettings", newParams];
          case "Plan":
            return ["BusinessTasks", newParams];
        }
      }

      return args;
    },
    [route.params],
  );

  const navigate = useCallback(
    (...args: ScreenParamsPair) => {
      navigation.navigate(...getNavigationArgs(...args));
    },
    [getNavigationArgs, navigation],
  );

  const push = useCallback(
    (...args: ScreenParamsPair) => {
      navigation.push(...getNavigationArgs(...args));
    },
    [getNavigationArgs, navigation],
  );

  const replace = useCallback(
    (...args: ScreenParamsPair) => {
      navigation.replace(...getNavigationArgs(...args));
    },
    [getNavigationArgs, navigation],
  );

  return useMemo(() => {
    return {
      ...navigation,
      navigate,
      push,
      replace,
    };
  }, [navigation, navigate, push, replace]);
};
